import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { fetchApi } from '@/common/lib/fetchApi'

export type CronRunStatus = 'requested' | 'running' | 'succeeded' | 'failed' | 'skipped'
export type CronRunTrigger = 'schedule' | 'manual'

export interface CronRun {
  id: number
  task_name: string
  status: CronRunStatus
  trigger: CronRunTrigger
  requested_by_employee_id: number | null
  requested_by_name: string | null
  requested_at: string
  started_at: string | null
  finished_at: string | null
  duration_seconds: number | null
  error_message: string | null
  summary: Record<string, unknown> | null
}

export interface CronTask {
  name: string
  label: string
  description: string
  enabled: boolean
  disabled_reason: string | null
  last_run: CronRun | null
}

/** Statuses that mean the cron service has not finished with the run yet. */
const IN_FLIGHT: CronRunStatus[] = ['requested', 'running']

export function isInFlight(run: CronRun | null): boolean {
  return run != null && IN_FLIGHT.includes(run.status)
}

/**
 * The registered cron tasks and the outcome of each one's last run.
 *
 * A queued run is claimed by the cron container on its next poll, so while any
 * run is in flight the list refreshes on its own and the admin can watch the
 * run move from `requested` through to `succeeded` or `failed`.
 */
export function useCronTasks() {
  return useQuery({
    queryKey: ['admin', 'cron-tasks'],
    queryFn: () => fetchApi<CronTask[]>('/api/org/cron'),
    refetchInterval: (query) =>
      (query.state.data ?? []).some((task) => isInFlight(task.last_run)) ? 3000 : false,
  })
}

export function useTriggerCronTask() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (taskName: string) =>
      fetchApi<CronRun>(`/api/org/cron/${taskName}/run`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'cron-tasks'] })
    },
  })
}
